import { useMutation, useQuery } from '@apollo/client'
import {
    Autocomplete,
    Button,
    FormControl,
    Grid,
    InputLabel,
    TextField,
    Typography,
} from '@mui/material'
import isEqual from 'lodash.isequal'
import {
    Dispatch,
    FC,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react'
import useToast from 'shared/components/Toast/useToast'
import { UpdateCandidateStartDate } from 'shared/graphql/candidatePreferences.gql'
import { GetStartDateOptions } from 'shared/graphql/startDates.gql'
import { EditButtonContainer } from 'shared/style/styled'
import { getPxAsRem } from 'shared/utils'
import PreferenceSectionHeader from './PreferenceSectionHeader'
import { editModel } from './preference.types'

interface Props {
    handleEditArray: (sectionName: string, cancelEdit?: boolean) => any
    editArray: editModel[]
    sectionName: string
    preferStartDate: string[]
    candidateId: string
    setUnsavedChangesExist: Dispatch<SetStateAction<boolean>>
    refetch: () => void
}

const PreferDate: FC<Props> = ({
    preferStartDate,
    handleEditArray,
    editArray,
    sectionName,
    candidateId,
    setUnsavedChangesExist,
    refetch,
}) => {
    const [startDates, setStartDates] = useState<string[] | undefined>(
        preferStartDate
    )
    const [isEdit, setIsEdit] = useState<boolean>(false)
    const [mutate] = useMutation(UpdateCandidateStartDate)
    const toast = useToast()

    useEffect(() => {
        setStartDates(preferStartDate)
    }, [preferStartDate])

    useEffect(() => {
        let isSectionEditable: boolean =
            editArray.find((ele) => ele.sectionName === sectionName)?.isEdit ||
            false
        setIsEdit(isSectionEditable)
    }, [editArray, sectionName])

    const resetForm = useCallback(() => {
        setStartDates(preferStartDate)
    }, [setStartDates, preferStartDate])

    const unsavedChangesExist = useMemo(
        () => !isEqual(startDates, preferStartDate),
        [startDates, preferStartDate]
    )

    useEffect(() => {
        setUnsavedChangesExist(unsavedChangesExist)
    }, [unsavedChangesExist, setUnsavedChangesExist])

    const { data } = useQuery(GetStartDateOptions)

    const handleUpdate = async () => {
        try {
            const { data, errors } = await mutate({
                variables: {
                    candidateId,
                    startDates,
                },
            })
            if (data.updateCandidateStartDate.success) {
                await handleEditArray(sectionName, true)
                toast({
                    title: 'Success',
                    message: 'Preferred start date updated successfully',
                    severity: 'success',
                })
                setUnsavedChangesExist(false)
                refetch()
            } else {
                console.log(errors)
                toast({
                    severity: 'error',
                    title: 'Error',
                    message: 'Failed to update preferred start date',
                })
            }
        } catch (error) {
            console.log(error)
            toast({
                severity: 'error',
                title: 'Error',
                message: 'Failed to update start date',
            })
        }
    }

    return (
        <>
            <PreferenceSectionHeader
                handleEditArray={handleEditArray}
                sectionName={sectionName}
                title="Preferred Start Date"
                subTitle="What is your desired start date?"
            />
            <Grid container spacing={2}>
                <Grid item xs={4}>
                    {isEdit ? (
                        <FormControl
                            fullWidth
                            style={{
                                marginBottom: getPxAsRem(16),
                                display: 'flex',
                                justifyContent: 'center',
                                width: '100%',
                            }}
                        >
                            <Autocomplete
                                options={data?.candidateStartDates || []}
                                renderInput={(params) => (
                                    <TextField {...params} />
                                )}
                                multiple
                                value={startDates}
                                onChange={(event, value) => {
                                    setStartDates(value)
                                }}
                            />
                        </FormControl>
                    ) : (
                        <>
                            <InputLabel>Start Date</InputLabel>
                            <Typography variant="body1">
                                {startDates && startDates.length
                                    ? startDates.join(', ')
                                    : '-'}
                            </Typography>
                        </>
                    )}
                </Grid>

                {isEdit && (
                    <EditButtonContainer item xs={12}>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                handleEditArray(sectionName, true)
                                resetForm()
                            }}
                        >
                            Cancel
                        </Button>
                        <Button onClick={handleUpdate}>Save and Update</Button>
                    </EditButtonContainer>
                )}
            </Grid>
        </>
    )
}

export default PreferDate
